import { useEffect, useState, useCallback } from "react";
import { Plus, FileDown, Sparkles, Loader2, FileText, Megaphone } from "lucide-react";
import { api, apiErr, openPdf } from "@/lib/api";
import { toast } from "sonner";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { PageHeader, Loading, StatusBadge, EmptyState, AiDraftNotice } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const MIN_FIELDS = [
  ["date", "日時", "date"], ["place", "場所"], ["attendees", "出席者"], ["absentees", "欠席者"],
  ["chair", "委員長"], ["recorder", "記録者"], ["agenda", "検討事項", "ta"], ["discussion", "検討内容", "ta"],
  ["opinions", "意見", "ta"], ["decisions", "決定事項", "ta"], ["issues", "課題", "ta"],
  ["responsible", "担当者"], ["deadline", "期限"], ["notice_content", "職員周知内容", "ta"], ["next_plan", "次回予定"],
];

export default function Committees() {
  const { fiscalYear } = useFiscalYear();
  const [items, setItems] = useState(null);
  const [offices, setOffices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", office_id: "", scheduled_month: 4, scheduled_date: "", owner: "" });
  const [minOpen, setMinOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [minutes, setMinutes] = useState({});
  const [memo, setMemo] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(() => {
    if (!fiscalYear) return;
    api.get(`/committees?fiscal_year=${fiscalYear}`).then(({ data }) => setItems(data));
  }, [fiscalYear]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/offices").then(({ data }) => setOffices(data)); }, []);

  const create = async () => {
    if (!form.name || !form.office_id) { toast.error("委員会名と事業所は必須です"); return; }
    try { await api.post("/committees", { ...form, fiscal_year: fiscalYear }); toast.success("委員会を作成しました"); setOpen(false); setForm({ name: "", office_id: "", scheduled_month: 4, scheduled_date: "", owner: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openMinutes = async (c) => {
    setCurrent(c);
    const { data } = await api.get(`/committees/${c.id}/minutes`);
    setMinutes(data && data.committee_id ? data : { committee_id: c.id });
    setMemo(""); setMinOpen(true);
  };

  const saveMinutes = async () => {
    try {
      await api.post(`/committees/${current.id}/minutes`, { ...minutes, committee_id: current.id });
      toast.success("議事録を保存しました"); setMinOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const aiMinutes = async () => {
    if (!memo.trim()) { toast.error("会議メモを入力してください"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/minutes", { committee_name: current.name, memo });
      if (data.data) { setMinutes((m) => ({ ...m, ...data.data })); toast.success("AIが議事録案を作成しました（内容を確認してください）"); }
      else toast.error("AI出力を解析できませんでした");
    } catch (e) { toast.error(apiErr(e)); } finally { setAiLoading(false); }
  };

  const broadcast = async () => {
    if (!minutes.notice_content) { toast.error("職員周知内容がありません"); return; }
    try {
      await api.post("/staff-notifications", { title: `${current.name}の周知`, body: minutes.notice_content, office_id: current.office_id, source_type: "committee", source_id: current.id, all_office_staff: true });
      toast.success("職員へ周知しました");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const savePdf = async () => {
    await api.post(`/committees/${current.id}/minutes`, { ...minutes, committee_id: current.id });
    const { data } = await api.get(`/committees/${current.id}/minutes`);
    if (data.id) openPdf(`/reports/committee/${data.id}`);
  };

  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader
        title="委員会" subtitle={`${fiscalYear}年度の委員会・議事録・職員周知`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-committee-btn"><Plus className="w-4 h-4 mr-1" /> 委員会を作成</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>委員会を作成</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>委員会名 *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="committee-name-input" /></div>
                <div><Label>事業所 *</Label>
                  <Select value={form.office_id} onValueChange={(v) => setForm({ ...form, office_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="committee-office-select"><SelectValue placeholder="選択" /></SelectTrigger>
                    <SelectContent>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>予定月</Label><Input type="number" min="1" max="12" value={form.scheduled_month} onChange={(e) => setForm({ ...form, scheduled_month: Number(e.target.value) })} className="mt-1" /></div>
                  <div><Label>予定日</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="mt-1" /></div>
                </div>
                <div><Label>担当者</Label><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="mt-1" /></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-blue-600 hover:bg-blue-700" data-testid="save-committee-btn">作成</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {items.length ? items.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-3 p-4" data-testid={`committee-row-${c.id}`}>
            <div>
              <div className="font-medium text-slate-800">{c.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">予定：{c.scheduled_date || `${c.scheduled_month}月`}／担当：{c.owner || "-"}</div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={c.status} />
              {c.has_minutes && <span className="text-xs text-green-600 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> 議事録あり</span>}
              <Button size="sm" variant="outline" onClick={() => openMinutes(c)} data-testid={`minutes-btn-${c.id}`}>議事録</Button>
            </div>
          </div>
        )) : <EmptyState title="委員会がありません" />}
      </div>

      <Dialog open={minOpen} onOpenChange={setMinOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{current?.name} 議事録</DialogTitle></DialogHeader>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-2">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-amber-600" /><span className="text-sm font-medium text-amber-800">AI議事録作成</span></div>
            <Textarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="会議メモを貼り付けてください" className="bg-white" data-testid="ai-memo-input" />
            <Button size="sm" onClick={aiMinutes} disabled={aiLoading} className="mt-2 bg-amber-500 hover:bg-amber-600" data-testid="ai-minutes-btn">{aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "AIで整理"}</Button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {MIN_FIELDS.map(([k, label, type]) => (
              <div key={k} className={type === "ta" ? "sm:col-span-2" : ""}>
                <Label>{label}</Label>
                {type === "ta"
                  ? <Textarea value={minutes[k] || ""} onChange={(e) => setMinutes({ ...minutes, [k]: e.target.value })} className="mt-1" data-testid={`min-${k}`} />
                  : <Input type={type === "date" ? "date" : "text"} value={minutes[k] || ""} onChange={(e) => setMinutes({ ...minutes, [k]: e.target.value })} className="mt-1" data-testid={`min-${k}`} />}
              </div>
            ))}
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={broadcast} data-testid="broadcast-btn"><Megaphone className="w-4 h-4 mr-1" /> 職員へ周知</Button>
            <Button variant="outline" onClick={savePdf} data-testid="minutes-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> PDF出力</Button>
            <Button onClick={saveMinutes} className="bg-blue-600 hover:bg-blue-700" data-testid="save-minutes-btn">保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
